import styled from 'styled-components';

import { ActionBar, ActionBarElementType, Scroll, StyledFlex } from '@dreryk/react-components';

import { visitPaths } from '~libs.desktop.shared.paths';
import { isVisitHistoryWithConfidentialDetails } from '~libs.shared.visit';

import { useAppointmentOutOfScheduleButton } from '../appointment/buttons/AppointmentOutOfScheduleButton/AppointmentOutOfScheduleButton';
import { VisitHeader } from '../common/visit/header/VisitHeader';
import { VisitHeaderPatientDetails } from '../common/visit/header/VisitHeaderPatientDetails';
import { VisitHeaderPatientNameLink } from '../common/visit/header/VisitHeaderPatientNameLink';
import { useVisitDependenciesContext } from '../dependencies';
import { visitTranslationKeys } from '../translations';
import VisitSettlement from '../visitSettlement';

import { VisitHistoryInfiniteList } from './VisitHistoryInfiniteList';
import { VisitHistoryInfiniteListLoader } from './VisitHistoryInfiniteListLoader';
import { VisitHistoryPageLoader } from './VisitHistoryPageLoader';
import { VisitHistoryPanel } from './VisitHistoryPanel';
import { VisitHistoryPanelLoader } from './VisitHistoryPanelLoader';
import { VisitHistoryWithoutConfidentialDetailsPanel } from './VisitHistoryWithoutConfidentialDetailsPanel';
import { VisitHistoryShowAnnulledCheckbox } from './VisitHistoryShowAnnulledCheckbox/VisitHistoryShowAnnulledCheckbox';

const StyledVisitHistoryPage = styled.div`
   display: flex;
   flex-direction: column;
   flex: 1;
   gap: 12px;
   height: 100%;
`;

StyledVisitHistoryPage.displayName = 'StyledVisitHistoryPage';

// eslint-disable-next-line max-lines-per-function
export const VisitHistoryPage = (): JSX.Element => {
   const { patientId } = visitPaths.useVisitHistoryParams();
   const { visitId } = visitPaths.useVisitHistorySearchParams();
   const { accountConfigurationVerificationEventInterceptor } = useVisitDependenciesContext();
   const AppointmentOutOfScheduleButton = useAppointmentOutOfScheduleButton();

   return (
      <ActionBar id='visit-history-action-bar' testId='visit-history'>
         <VisitHistoryPageLoader patientId={patientId}>
            {({ patient }) => (
               <>
                  <StyledVisitHistoryPage>
                     <StyledFlex justifyContent={'space-between'} gap={'8px'}>
                        <VisitHeader
                           title={
                              <VisitHeaderPatientNameLink
                                 patient={patient}
                                 backActionLabel={visitTranslationKeys.goBackToVisitHistory}
                              />
                           }
                           details={<VisitHeaderPatientDetails patient={patient} />}
                        />
                        <VisitHistoryShowAnnulledCheckbox />
                     </StyledFlex>
                     {visitId ? (
                        <VisitHistoryPanelLoader visitId={visitId} patientId={patientId}>
                           {({ visitHistory }) => (
                              <>
                                 {isVisitHistoryWithConfidentialDetails(visitHistory) ? (
                                    <>
                                       <VisitSettlement.MedicalServicesCounterResolver
                                          appointmentIds={[visitHistory.appointmentId]}
                                       />
                                       <Scroll testId={'scroll'}>
                                          <VisitHistoryPanel visitHistory={visitHistory} patientId={patientId} />
                                       </Scroll>
                                    </>
                                 ) : (
                                    <VisitHistoryWithoutConfidentialDetailsPanel visitHistory={visitHistory} />
                                 )}
                              </>
                           )}
                        </VisitHistoryPanelLoader>
                     ) : (
                        <VisitHistoryInfiniteListLoader patientId={patientId}>
                           {({ visitHistoryPages, fetchNextVisitHistoryPage, nextVisitHistoryPageAvailable }) => {
                              const partialAppointmentIds = visitHistoryPages
                                 .at(-1)!
                                 .map(({ appointmentId }) => appointmentId);

                              return (
                                 <>
                                    <VisitSettlement.MedicalServicesCounterResolver
                                       appointmentIds={partialAppointmentIds}
                                    />
                                    <VisitHistoryInfiniteList
                                       patientId={patientId}
                                       data={visitHistoryPages}
                                       onLoadMore={fetchNextVisitHistoryPage}
                                       loadMoreAvailable={nextVisitHistoryPageAvailable}
                                    />
                                 </>
                              );
                           }}
                        </VisitHistoryInfiniteListLoader>
                     )}
                  </StyledVisitHistoryPage>

                  {AppointmentOutOfScheduleButton ? (
                     <ActionBar.Portal elementType={ActionBarElementType.PRIMARY}>
                        <AppointmentOutOfScheduleButton
                           patientId={patient.id}
                           accountConfigurationVerificationEventInterceptor={
                              accountConfigurationVerificationEventInterceptor
                           }
                           compact
                        />
                     </ActionBar.Portal>
                  ) : null}
               </>
            )}
         </VisitHistoryPageLoader>

         <ActionBar.GoBackActionLink
            label={visitTranslationKeys.goBackToAppointmentsList}
            to={visitPaths.getAppointmentsListPath()}
         />
      </ActionBar>
   );
};
